import {
  Alert,
  Check,
  Clock,
  ClockBack,
  Cross,
  Spinner,
} from '@factorialco/factorial-one-react/icons/app'
import {
  IntegrationsSyncableState,
  IntegrationsSyncableStateStatusEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

const isPending = (syncableStates: Array<IntegrationsSyncableState | undefined>) =>
  syncableStates.every((syncableState) => syncableState === undefined)

const hasSyncing = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) =>
  statuses.has('syncing')

const hasSynced = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) =>
  statuses.has('synced')

const hasOutdated = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) =>
  statuses.has('outdated')

const hasFailed = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) =>
  statuses.has('failed') || statuses.has('invalid')

const hasUndefined = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) =>
  statuses.has(undefined)

const isPartiallySynced = (statuses: Set<IntegrationsSyncableStateStatusEnum | undefined>) => {
  return (
    (hasSynced(statuses) && (hasFailed(statuses) || hasUndefined(statuses))) ||
    (hasFailed(statuses) && (hasSynced(statuses) || hasUndefined(statuses)))
  )
}

export const getBadgeGroupedBySyncables = (
  isSyncRunning: boolean,
  syncableStates: Array<IntegrationsSyncableState | undefined>
) => {
  const syncingBagde = {
    type: 'neutral',
    icon: Spinner,
    tooltip: i18n.t('integrations.sync_tooltips.syncing'),
  }

  const pendingBagde = {
    type: 'neutral',
    icon: Clock,
    tooltip: i18n.t('integrations.sync_tooltips.pending'),
  }

  const outdatedBagde = {
    type: 'neutral',
    icon: ClockBack,
    tooltip: i18n.t('integrations.sync_tooltips.outdated'),
  }

  const partiallySyncedBagde = {
    type: 'warning',
    icon: Alert,
    tooltip: i18n.t('integrations.sync_tooltips.partially_synced'),
  }

  const failedBagde = {
    type: 'critical',
    icon: Cross,
    tooltip: i18n.t('integrations.sync_tooltips.failed'),
  }

  const syncedBagde = {
    type: 'positive',
    icon: Check,
    tooltip: i18n.t('integrations.sync_tooltips.synced'),
  }

  if (isSyncRunning) return syncingBagde
  if (isPending(syncableStates)) return pendingBagde

  const statuses = new Set(syncableStates.map((syncableState) => syncableState?.status))

  if (hasOutdated(statuses)) return outdatedBagde
  if (isPartiallySynced(statuses)) return partiallySyncedBagde
  if (hasSyncing(statuses)) return syncingBagde
  if (hasFailed(statuses)) return failedBagde
  return syncedBagde
}
